/* eslint-disable max-len */
const split = require('./split.json');
const disbursementPurpose = require('./disbursement-purpose.json');
const deposit = require('./deposit.json');
const purpose = require('./purpose.json');
const receipt = require('./receipt.json');
const attachMember = require('./attach-member.json');
const security = require('./security.json');
const feeConfig = require('./fee-configuration-lookup.json');

/**
 * Sales Attach member validation.
 * No additional properties allowed.
 * @typedef {Object} SalesVxCoreAttachMember
 * @property {ObjectId} member - The member id. Required.
 * @property {string} type - The type. Required.
 */

/**
 * Deposit Schema.
 * No additional properties are allowed.
 * @typedef {Object} SalesVxCoreDeposit
 * @property {number} amount - The deposit amount. Required.
 * @property {string} type - The deposit type. Required.
 * @property {string} [description] - The description of the deposit. Optional.
 * @property {Number} [__v] the version should this be an update
 */

/**
 * Disbursement Purpose Schema.
 * No additional properties are allowed.
 * @typedef {Object} SalesVxCoreDisbursementPurpose
 * @property {number} amount - The split amount. Required.
 * @property {string} type - The split type. Required.
 * @property {string} [description] - The description of the split. Optional.
 * @property {string} [details] - The details of the split. Optional.
 * @property {boolean} [investment] - The investment flag. Optional.
 * @property {IdentityDocument} [identities] - The identities. Optional.
 * @property {Number} [__v] the version should this be an update
 */

/**
 * VX Product
 * No additional properties allowed.
 * @typedef {Object} SalesVxCoreProduct
 * @property {Term} term - Required.
 * @property {Term} [honeymoon]
 * @property {Term} [fixed]
 * @property {number} [balloonAmount]
 * @property {string} slug - Required.
 * @property {string} pricingSlug - Required.
 * @property {number} rate - Required.
 * @property {number} margin - Required.
 * @property {number} mdr - Required.
 * @property {Term} [interestOnly]
 * @property {boolean} [isCustomRate]
 * @property {boolean} [isCustomTerm]
 * @property {number} [adjustment]
 */

/**
 * Split Schema
 * No additional properties are allowed.
 * @typedef {Object} SalesVxCoreSplit
 * @property {number} amount - The split amount. Required. If missing, error message: "loan-amount-required".
 * @property {String} [repaymentStrategy]
 * @property {SalesVxCoreProduct} product - Required.
 * @property {Number} [__v] the version should this be an update
 */

/**
 * Brokerage Amount
 * No additional properties allowed.
 * @typedef {Object} BrokerageAmount
 * @property {number} base - Required.
 * @property {number} gst - Required.
 */

/**
 * External Brokerage
 * No additional properties allowed.
 * @typedef {Object} ExternalBrokerage
 * @property {BrokerageAmount} amount - Required.
 * @property {boolean} [isGstFinanced=false]
 */

/**
 * VX Purpose
 * No additional properties are allowed.
 * @typedef {Object} SalesVxCorePurpose
 * @property {ProductCategoryType} __t - Required.
 * @property {string} primaryPurpose - Required.
 * @property {string} [applicationType] - The application type. This is required when product category type, `__t`, is 'home-loan'.
 * @property {ExternalBrokerage} externalBrokerage - External Brokerage - Required
 * @property {Number} [__v] the version should this be an update
 */

/**
 * @typedef {Object} SalesVxCoreReceipt
 * No additional properties are allowed.
 * @property {number} amount - The receipt amount. Required.
 * @property {string} type - The receipt type. Required.
 * @property {string} [description] - The description of the receipt. Optional.
 * @property {Number} [__v] the version should this be an update
 */

/**
 * @typedef {Object} SalesVxCoreSecurityRealEstate
 * No additional properties allowed.
 * @property {string} primaryPurpose - The primary purpose of the security. Can be `null` if not provided.
 * @property {number} purchaseAmount - The purchase amount of the real estate.
 * @property {number} constructionAmount - The construction amount of the real estate.
 * @property {boolean} [isBeingPurchased=false] - Indicates whether it's being purchased.
 * @property {boolean} [receivedSolarInvoice=false] - Indicates whether the solar invoice has been received.
 * @property {number} collateralId - The ID of collateral. Required.
 * @property {string} __t - The type of the security ("real-estate"). Required.
 * @property {Number} [__v] the version should this be an update
 */

/**
 * @typedef {Object} SalesVxCoreSecurityVehicle
 * No additional properties allowed.
 * @property {boolean} [isGreen=false] - Indicates whether the vehicle security is green.
 * @property {number} collateralId - The ID of collateral. Required.
 * @property {string} __t - The type of the security ("vehicle"). Required.
 * @property {Number} [__v] the version should this be an update
 */

/**
 * @typedef {SalesVxCoreSecurityRealEstate|SalesVxCoreSecurityVehicle} SalesVxCoreSecurity
 * No additional properties allowed.
 */

/**
 * @typedef {Object} SalesVxFeeConfig
 * @property {string} feeType - Required (origination | application)
 * @property {string} channel - Required
 * @property {string} productCategoryType - Required
 * @property {string} [purpose] - Conditionally required (required when feeType === 'application')
 */

/**
 * Sale related validation schemas for vxcore
 * @module schemas/sales/vxcore
 * @property {SalesVxCoreSplit} split
 * @property {SalesVxCoreDisbursementPurpose} disbursementPurpose
 * @property {SalesVxCoreDeposit} deposit
 * @property {SalesVxCorePurpose} purpose
 * @property {SalesVxCoreReceipt} receipt
 * @property {SalesVxCoreAttachMember} attachMember
 * @property {SalesVxCoreSecurity} security
 * @property {SalesVxFeeConfig} feeConfig
 */
module.exports = {
  split,
  disbursementPurpose,
  deposit,
  purpose,
  receipt,
  attachMember,
  security,
  feeConfig,
};
